import {
  ComponentRef,
  EnvironmentInjector,
  inject,
  Injectable,
  Type,
  ViewContainerRef
} from '@angular/core';
import { SidenavOption } from '@aid/core/types/interfaces';
import { BehaviorSubject } from 'rxjs';
import { FeaturesService } from '@aid/core/services/features.service';
import { Features, UserPermission } from '@aid/core/types/enums';
import { Organization } from '@aid/core/types/classes';
import { UserService } from '@aid/core/services/user.service';

@Injectable({
  providedIn: 'root'
})
export class SidenavService {
  /**
   * View container ref of the content from the sidenav. Dynamic components
   * should be injected in this view container ref.
   */
  public sidenavContent?: ViewContainerRef;

  private readonly DEFAULT_SIDENAV_MAX_WIDTH = 500;
  readonly SIDENAV_MAX_WIDTH = 600;

  private readonly featuresService = inject(FeaturesService);
  private readonly userService = inject(UserService);

  public opened$ = new BehaviorSubject<boolean>(false);
  public width$ = new BehaviorSubject<number>(this.DEFAULT_SIDENAV_MAX_WIDTH);

  public buildSidenavOptions(organization: Organization): SidenavOption[] {
    const organizationSlug = organization.slug;
    const options: SidenavOption[] = [
      {
        name: 'dashboard',
        icon: 'new_grid',
        routerLink: `${organizationSlug}/`,
        permissions: []
      },
      {
        name: 'damage',
        icon: 'fire',
        routerLink: `${organizationSlug}/damages`,
        permissions: []
      },
      {
        name: 'reminders',
        icon: 'calendar',
        routerLink: `${organizationSlug}/reminders`,
        permissions: []
      },
      {
        name: 'customers',
        icon: 'people',
        routerLink: `${organizationSlug}/customers`,
        permissions: []
      },
      {
        name: 'insurances',
        icon: 'insurance',
        routerLink: `${organizationSlug}/insurances`,
        permissions: []
      },
      {
        name: 'users',
        icon: 'person',
        routerLink: `${organizationSlug}/management`,
        permissions: ['can_view_users']
      },
      {
        name: 'contact-persons',
        icon: 'info',
        routerLink: `${organizationSlug}/contact-persons`,
        permissions: ['can_view_contact_persons']
      },
      /*{
        name: 'information',
        icon: 'info',
        routerLink: `${organizationSlug}/information`,
        permissions: []
      },
      {
        name: 'contact',
        icon: 'email',
        routerLink: `${organizationSlug}/contact`,
        permissions: []
      },*/
      {
        name: 'partnerships',
        icon: 'new_keypad',
        routerLink: `${organizationSlug}/partnerships`,
        permissions: ['administrators']
      },
      {
        name: 'GDV',
        icon: 'file_text',
        routerLink: `${organizationSlug}/gdv`,
        permissions: ['administrators']
      },
      {
        name: 'accounting.label',
        icon: 'percent',
        routerLink: `${organizationSlug}/accounting`,
        permissions: [UserPermission.CAN_MANAGE_ACCOUNTING]
      },
      {
        name: 'reports',
        icon: 'pie_chart',
        routerLink: `${organizationSlug}/reports`,
        permissions: [UserPermission.CAN_MANAGE_ACCOUNTING]
      },
      {
        name: 'import',
        icon: 'upload',
        routerLink: `${organizationSlug}/import`,
        permissions: ['administrators']
      },
      {
        name: 'billing.label',
        icon: 'credit_card',
        routerLink: `${organizationSlug}/billing`,
        permissions: []
      },
      {
        name: 'administration',
        icon: 'settings',
        routerLink: `${organizationSlug}/administration`,
        permissions: ['administrators']
      }
    ];

    if (!this.featuresService.isFeatureAvailable(Features.GDV)) {
      const index = options.findIndex(value => value.name === 'GDV');
      options.splice(index, 1);
    }

    if (!this.featuresService.isFeatureAvailable(Features.ACCOUNTING)) {
      const index = options.findIndex(
        value => value.name === 'accounting.label'
      );
      options.splice(index, 1);
    }

    if (!this.featuresService.isFeatureAvailable(Features.REPORTS)) {
      const index = options.findIndex(value => value.name === 'reports');
      options.splice(index, 1);
    }

    if (organization.withoutSubscription || !this.userService.isOwner) {
      const index = options.findIndex(value => value.name === 'billing.label');
      options.splice(index, 1);
    }

    return options;
  }

  setWidth(size: number) {
    this.width$.next(size);
  }

  open<T>(
    component: Type<T>,
    environmentInjector: EnvironmentInjector,
    width?: number
  ): ComponentRef<T> {
    // The global container owns the view; the caller owns lazy-feature providers.
    const componentRef = this.requireSidenavContent().createComponent(
      component,
      {
        environmentInjector
      }
    );
    this.opened$.next(true);
    if (width) {
      this.width$.next(width);
    }
    return componentRef;
  }

  close() {
    this.requireSidenavContent().clear();
    this.opened$.next(false);
    this.width$.next(this.DEFAULT_SIDENAV_MAX_WIDTH);
  }

  private requireSidenavContent(): ViewContainerRef {
    if (!this.sidenavContent) {
      throw new Error('The sidenav content container has not been registered.');
    }
    return this.sidenavContent;
  }

  get isOpened$() {
    return this.opened$.asObservable();
  }

  get isOpenedValue() {
    return this.opened$.value;
  }
}
